import type { SplitCategory } from '@/api/dashboard-live';
import { usePilotage } from '../context';
import { BigNumber, DataTable, Panel, Row, SectionState, StackBar, Unavailable } from '../ui';
import { DailyChart, Legend } from '../charts';
import { dateTime, dec, num, pct } from '../format';
import { migrationProjectsDetail, migrationRateDetail, opsDetail, splitColors, splitDetail, SPLIT_LABEL } from '../details';

/** Écran 3 — Migration V1 → V2 : taux, répartition des écoles, projets */
export function MigrationScreen() {
  const { payload, palette: p, openDetail } = usePilotage();
  const plat = payload.sections.platform?.data;
  if (!plat) return <Unavailable section={payload.sections.platform} what="Migration" />;

  const colors = splitColors(p);
  const cats = Object.keys(SPLIT_LABEL) as SplitCategory[];
  const m = plat.migration;
  const done = m.records.total > 0 ? (100 * m.records.processed) / m.records.total : null;

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-3 xl:grid-cols-12 xl:grid-rows-[auto_minmax(0,1fr)_minmax(0,1fr)]">
      <Panel title="Part d'usage V2" className="xl:col-span-3" onOpen={() => openDetail(migrationRateDetail(payload, p))}>
        <BigNumber value={dec(plat.rates.usageV2Pct)} unit="%" className="text-5xl" />
        <p className="mt-1 text-xs text-slate-400">
          {num(plat.rates.v2Dau)} actifs V2 · {num(plat.rates.v1Dau)} actifs V1
        </p>
      </Panel>
      <Panel title="Écoles basculées" subtitle="≥ 80 % des actifs du jour en V2" className="xl:col-span-3" onOpen={() => openDetail(splitDetail(payload, p))}>
        <BigNumber value={dec(plat.rates.schoolsMostlyV2Pct ?? plat.rates.schoolsV2OnlyPct, 0)} unit="%" className="text-5xl" />
        <p className="mt-1 text-xs text-slate-400">
          {num(plat.rates.schoolsMostlyV2)} / {num(plat.rates.schoolsActive)} écoles actives · dont {num(plat.split.counts.v2Only)} sans aucune connexion V1
        </p>
      </Panel>
      <Panel title="Parc équipé V2" subtitle="déploiement V2 en production" className="xl:col-span-3" onOpen={() => openDetail(splitDetail(payload, p))}>
        <BigNumber value={dec(plat.rates.fleetV2Pct, 0)} unit="%" className="text-5xl" />
        <p className="mt-1 text-xs text-slate-400">
          {num(plat.fleet.v2Production)} / {num(plat.fleet.total)} écoles · {num(plat.fleet.v1OnlyProduction)} V1 sans V2
        </p>
      </Panel>
      <Panel title="Projets de migration" className="xl:col-span-3" onOpen={() => openDetail(migrationProjectsDetail(payload))}>
        <div className="grid grid-cols-2 gap-x-4">
          <Row label="Terminés" value={num(m.projects.completed)} />
          <Row label="En cours" value={num(m.projects.running)} />
          <Row label="En échec" value={num(m.projects.failed)} />
          <Row label="Brouillons" value={num(m.projects.draft)} />
        </div>
        <p className="mt-1 text-xs text-slate-400">
          {pct(done, 0)} des enregistrements traités · {num(m.stepsFailed)} lot(s) en échec
        </p>
      </Panel>

      <Panel
        title="Bascule de l'usage"
        subtitle="part d'usage V2 et écoles basculées, par jour"
        className="xl:col-span-7"
        right={
          <Legend
            defs={[
              { metric: 'migration.usage_v2_pct', label: 'Part d\'usage V2', color: p.v2 },
              { metric: 'migration.schools_mostly_v2_pct', label: 'Écoles basculées', color: p.third },
            ]}
          />
        }
      >
        <DailyChart
          defs={[
            { metric: 'migration.usage_v2_pct', label: 'Part d\'usage V2', color: p.v2 },
            { metric: 'migration.schools_mostly_v2_pct', label: 'Écoles basculées', color: p.third },
          ]}
          format={(n) => `${dec(n, 0)} %`}
          domainMax={100}
          height="100%"
        />
      </Panel>
      <Panel title="Où se connectent les écoles aujourd'hui" className="xl:col-span-5" right={<SectionState section={payload.sections.platform} />}>
        <div className="space-y-3">
          <StackBar segments={cats.map((k) => ({ key: k, label: SPLIT_LABEL[k], value: plat.split.counts[k], color: colors[k] }))} height={22} />
          <div className="grid grid-cols-2 gap-2">
            {cats.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => openDetail(splitDetail(payload, p, k))}
                className="rounded-lg border border-slate-200 p-2 text-left hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-600"
              >
                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colors[k] }} aria-hidden />
                  {SPLIT_LABEL[k]}
                </div>
                <div className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{num(plat.split.counts[k])}</div>
              </button>
            ))}
          </div>
        </div>
      </Panel>

      <Panel
        title="Répartition des écoles par jour"
        className="xl:col-span-5"
        right={
          <Legend
            defs={[
              { metric: 'split.v2_only', label: SPLIT_LABEL.v2Only, color: colors.v2Only },
              { metric: 'split.both', label: SPLIT_LABEL.both, color: colors.both },
              { metric: 'split.v1_only', label: SPLIT_LABEL.v1Only, color: colors.v1Only },
            ]}
          />
        }
      >
        <DailyChart
          defs={[
            { metric: 'split.v2_only', label: SPLIT_LABEL.v2Only, color: colors.v2Only },
            { metric: 'split.both', label: SPLIT_LABEL.both, color: colors.both },
            { metric: 'split.v1_only', label: SPLIT_LABEL.v1Only, color: colors.v1Only },
          ]}
          bars
          stacked
          days={30}
          height="100%"
        />
      </Panel>
      <Panel title="Écoles encore en V1 aujourd'hui" subtitle="à accompagner" className="xl:col-span-4" bodyClassName="overflow-y-auto" onOpen={() => openDetail(splitDetail(payload, p, 'v1Only'))}>
        <DataTable
          rows={plat.split.schools.filter((x) => x.category === 'v1Only' || (x.category === 'both' && (x.v2SharePct ?? 0) < 50))}
          max={14}
          empty="Aucune école majoritairement en V1"
          columns={[
            { key: 'c', label: 'École', render: (r) => <span title={r.name}>{r.code}</span> },
            { key: 'v1', label: 'V1', align: 'right', render: (r) => num(r.v1Users) },
            { key: 'v2', label: 'V2', align: 'right', render: (r) => num(r.v2Users) },
            { key: 's', label: 'Part V2', align: 'right', render: (r) => pct(r.v2SharePct, 0) },
          ]}
        />
      </Panel>
      <Panel title="Derniers projets" className="xl:col-span-3" bodyClassName="overflow-y-auto" onOpen={() => openDetail(opsDetail(payload))}>
        <div className="space-y-1">
          {m.recent.slice(0, 10).map((r) => (
            <Row key={r.code + r.startedAt} label={<span title={r.name}>{r.code}</span>} value={r.status} hint={dateTime(r.completedAt ?? r.startedAt)} />
          ))}
        </div>
      </Panel>
    </div>
  );
}
